import { z } from 'zod';

export const PRODUCT_KINDS = ['subscription', 'pass', 'credits', 'coins', 'story_pack', 'outfit', 'starter_bundle'] as const;
export const ProductKind = z.enum(PRODUCT_KINDS);
export type ProductKind = z.infer<typeof ProductKind>;

/** Membership tiers, lowest first: free, 月卡 (pass), Inked Plus and Inked Premium (one subscription group). */
export const TIERS = ['free', 'pass', 'plus', 'premium'] as const;
export const Tier = z.enum(TIERS);
export type Tier = z.infer<typeof Tier>;

/**
 * Membership numbers the app shows and the server enforces (Store v2, docs/STORE_V2.md). AI chapters
 * are the main running cost, so they have a daily allowance (chapters written for you, invites out and
 * what-if replays all count); neighbour messages and new voice lines do too. A chapter ticket is one
 * full chapter (8 drawn pages). Ink coins arrive every day of a membership on their own (App Store
 * 3.1.2(a): nothing paid for waits on a check-in); a 月卡 and a subscription held together each give theirs.
 */
export const MEMBERSHIP = {
  dailyAiChapters: { free: 3, pass: 5, plus: 8, premium: 10 },
  /** Messages a player can send to neighbours (NPC chats, all together) per game day. */
  dailyNpcMessages: { free: 20, pass: 50, plus: 150, premium: 500 },
  /** Ink coins each game day of a membership. */
  dailyCoins: { free: 0, pass: 100, plus: 300, premium: 500 },
  maxEnergy: { free: 12, pass: 20, plus: 30, premium: 45 },
  /** Chapter tickets a 月卡 gives the moment it's bought. */
  passTickets: 1,
  /** Chapter tickets each paid month of a subscription brings (with the purchase and every renewal). */
  monthlyTickets: { plus: 3, premium: 7 },
  /** Monthly instalments in a yearly plan (retired; kept for any old purchase). */
  plusYearInstalments: 12,
  /** Newly voiced lines per member per game day (replaying a line already voiced is free and uncounted). */
  dailyNewVoiceLines: 20,
  /** Free work shifts per game day across all jobs (each job at most once a day). */
  dailyShifts: { free: 2, pass: 2, plus: 3, premium: 3 },
  passDays: 30,
} as const;

/** Every player earns a little: finishing this many story chapters in a week (Monday 5 am, their city) gives these coins once. */
export const WEEKLY_STORY_REWARD = { stories: 5, coins: 300 } as const;

export const CatalogItem = z.object({
  productId: z.string(),
  kind: ProductKind,
  title: z.string(),
  description: z.string(),
  perks: z.array(z.string()),
  /** Chapter tickets (wallet credits) it gives: a pack's tickets, a 月卡's at purchase, a subscription's each paid month. */
  credits: z.number().int(),
  /** Ink coins it gives at purchase (retired coin packs and bundle only). */
  coins: z.number().int(),
  /** Ink coins each game day while a membership runs (月卡, Plus, Premium); 0 for everything else. */
  dailyCoins: z.number().int(),
  /** The membership tier it gives (月卡 = pass, a subscription = plus or premium); null for packs. */
  tier: Tier.nullable(),
  /** Subscription billing period; null for one-off purchases. */
  period: z.enum(['week', 'month', 'year']).nullable(),
  storeProductId: z.string(),
  imageUrl: z.string().nullable(),
  /** Display-only; the store sheet is the price of record. */
  priceHint: z.string(),
  badge: z.enum(['best_value', 'popular', 'limited']).nullable(),
  owned: z.boolean(),
  eligible: z.boolean(),
});
export type CatalogItem = z.infer<typeof CatalogItem>;

export const WalletView = z.object({
  /** Spendable chapter tickets (wallet credits), test ones included (they are spent first). */
  credits: z.number().int(),
  /** The part of `credits` that came from store sandbox (TestFlight) purchases: never real balance, 0 for everyone but testers. */
  testCredits: z.number().int(),
  reservedCredits: z.number().int(),
  coins: z.number().int(),
  /** The best membership running now: what the day's allowances follow. */
  tier: Tier,
  /** The subscription (Inked Plus or Inked Premium): one at a time, one store group. */
  plus: z.object({
    active: z.boolean(),
    /** Which plan runs: Inked Plus or Inked Premium; null when none does. */
    tier: z.enum(['plus', 'premium']).nullable(),
    renewsAt: z.string().nullable(),
    /** Chapter tickets each paid month of the running plan brings (0 when none runs). */
    monthlyTickets: z.number().int(),
    /** The store couldn't renew: Plus stays on until this time while the player fixes their payment. */
    graceUntil: z.string().nullable(),
    /** The plan that takes over at the next renewal (store product id), after a plan change. */
    pendingProductId: z.string().nullable(),
    /** The running plan (store product id), and where it was bought: 'stripe' is managed in the website's billing portal. */
    productId: z.string().nullable(),
    source: z.enum(['store', 'stripe', 'other']).nullable(),
    /** Plus from a store sandbox (TestFlight) purchase: testers only. */
    sandbox: z.boolean(),
    /** A second Plus subscription paid at the same time, and where (the player is billed twice); null normally. */
    alsoBilledBy: z.enum(['store', 'stripe', 'other']).nullable(),
  }),
  /** 月卡: 30 days of daily coins, more chats and chapters, and a bigger energy tank. */
  pass: z.object({ active: z.boolean(), expiresAt: z.string().nullable() }),
  /** Today's membership coins: how many, and whether they're still on their way (the server delivers them, no visit needed). */
  daily: z.object({ coins: z.number().int(), claimable: z.boolean() }),
  /** AI-written chapters started today against the day's allowance. */
  aiChapters: z.object({ used: z.number().int(), limit: z.number().int() }),
  /** Basic manga (composed from approved art) is always free; a full chapter costs a ticket. */
  basicMangaFree: z.boolean(),
  /** Tickets one full chapter costs (1). */
  illustratedCost: z.number().int(),
  /** The player's first full chapter is on us, and it's still to come. */
  freeFullChapter: z.boolean(),
  entitlements: z.array(z.object({ key: z.string(), expiresAt: z.string().nullable() })),
});
export type WalletView = z.infer<typeof WalletView>;

export const LedgerEntryView = z.object({
  id: z.string(),
  kind: z.enum(['grant', 'purchase', 'reserve', 'settle', 'release', 'refund', 'adjust', 'reward']),
  /** Real and test credits together. */
  amount: z.number().int(),
  /** 'sandbox': came from or went to test credits (store sandbox purchases). */
  environment: z.enum(['production', 'sandbox']),
  reason: z.string(),
  /** A website purchase: what was paid, in minor units of the currency (website history only). */
  paid: z.object({ amount: z.number().int(), currency: z.string() }).nullable().optional(),
  createdAt: z.string(),
});
export type LedgerEntryView = z.infer<typeof LedgerEntryView>;

export const VerifyPurchaseRequest = z.object({
  /** The RevenueCat app user id is always our userId, so the server resolves by syncing that customer. */
  storeProductId: z.string().max(120),
  transactionId: z.string().max(200),
});
export type VerifyPurchaseRequest = z.infer<typeof VerifyPurchaseRequest>;

/**
 * The state after syncing, not only what this call applied (the webhook may have got there first):
 * `synced` grants applied now, `recorded` whether the asked-about transaction is granted, `plus` whether Plus is on.
 */
export const VerifyPurchaseResult = z.object({ synced: z.number().int(), recorded: z.boolean(), plus: z.boolean() });
export type VerifyPurchaseResult = z.infer<typeof VerifyPurchaseResult>;

export const OutfitView = z.object({
  id: z.string(),
  name: z.string(),
  premium: z.boolean(),
  owned: z.boolean(),
  equipped: z.boolean(),
  coinPrice: z.number().int().nullable(),
  previewUrl: z.string(),
});
export type OutfitView = z.infer<typeof OutfitView>;
export const OutfitPurchaseResult = z.object({ coins: z.number().int(), ownedOutfit: z.string() });
export type OutfitPurchaseResult = z.infer<typeof OutfitPurchaseResult>;

const CoinRange = z.object({ min: z.number().int(), max: z.number().int() });
/** What ink coins buy and how they're earned, computed from the live catalogue and content. */
export const CoinGuide = z.object({
  spend: z.object({ outfit: CoinRange, furniture: CoinRange, pet: CoinRange, tip: CoinRange }),
  earn: z.object({ work: CoinRange, cafe: CoinRange, goals: CoinRange }),
});
export type CoinGuide = z.infer<typeof CoinGuide>;

/** Currencies the website sells in (Stripe presentment currencies; the account settles in AUD). */
export const WEB_CURRENCIES = ['hkd', 'twd', 'aud', 'nzd', 'sgd', 'myr', 'mop', 'cny', 'usd'] as const;
export const WebCurrency = z.enum(WEB_CURRENCIES);
export type WebCurrency = z.infer<typeof WebCurrency>;

/** A product as the website sells it: the app catalogue item plus its Stripe price in the chosen currency. */
export const WebCatalogItem = CatalogItem.extend({
  /** Minor units (cents; whole dollars ×100 for zero-decimal-looking currencies like TWD). null: not on sale in this currency. */
  amount: z.number().int().nullable(),
  currency: WebCurrency,
});
export type WebCatalogItem = z.infer<typeof WebCatalogItem>;

/** The pre-registration launch gift, while the buyer has it: one website purchase at this much off. */
export const WebGift = z.object({ percentOff: z.number().int().min(1).max(100) });
export type WebGift = z.infer<typeof WebGift>;

export const WebCatalog = z.object({ currency: WebCurrency, items: z.array(WebCatalogItem), gift: WebGift.nullable() });
export type WebCatalog = z.infer<typeof WebCatalog>;

export const WebCheckoutRequest = z.object({
  productId: z.string().max(60),
  currency: WebCurrency,
  /** Spend the launch gift on this purchase. */
  gift: z.boolean().default(false),
});
export type WebCheckoutRequest = z.input<typeof WebCheckoutRequest>;
/** Where to send the buyer: Stripe Checkout (or the billing portal). */
export const RedirectUrl = z.object({ url: z.string() });
export type RedirectUrl = z.infer<typeof RedirectUrl>;
